import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createSpeakerProfile } from '../lib/speakerService';

const SpeakerApplyPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    name: user?.user_metadata?.full_name || user?.email?.split('@')[0] || '',
    title: '',
    church_name: '',
    city: '',
    state: '',
    country: 'United States',
    organization: '',
    bio: '',
    topics: '',
    website: '',
    facebook_url: '',
    instagram_url: '',
    youtube_url: '',
    twitter: '',
    linkedin: '',
    youtube_video_id: '',
    book_url: '',
    amazon_url: '',
    resources_url: '',
    photo_url: '',
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/login');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const topicsArray = form.topics
        ? form.topics.split(',').map((t) => t.trim()).filter(Boolean)
        : [];

      const profileData = {
        name: form.name,
        title: form.title || null,
        church_name: form.church_name || null,
        city: form.city || null,
        state: form.state || null,
        country: form.country || null,
        organization: form.organization || null,
        bio: form.bio || null,
        topics: topicsArray.length > 0 ? topicsArray : null,
        website: form.website || null,
        facebook_url: form.facebook_url || null,
        instagram_url: form.instagram_url || null,
        youtube_url: form.youtube_url || null,
        twitter: form.twitter || null,
        linkedin: form.linkedin || null,
        youtube_video_id: form.youtube_video_id || null,
        book_url: form.book_url || null,
        amazon_url: form.amazon_url || null,
        resources_url: form.resources_url || null,
        photo_url: form.photo_url || null,
      };

      const { error: createError } = await createSpeakerProfile(profileData);

      if (createError) throw createError;

      setSuccess(true);
    } catch (err) {
      console.error('Error creating speaker profile:', err);
      setError(err.message || 'Could not create speaker profile. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900 min-h-screen pt-24">
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Create Speaker Profile</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Please log in or create an account to apply as a speaker.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition-colors"
          >
            Log In to Continue
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900 min-h-screen pt-24">
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">Profile Submitted!</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-2 max-w-lg mx-auto">
            Your speaker profile has been created as a draft. An ACP administrator will review and publish your profile.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-8">
            You'll be notified when your profile is approved. You can update your profile anytime from the Speaker Portal.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/speakers"
              className="inline-flex items-center px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition-colors"
            >
              Browse Speakers
            </Link>
            <Link
              to="/speaker"
              className="inline-flex items-center px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Go to Speaker Portal
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const inputClass = "w-full px-3 py-2.5 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent";
  const labelClass = "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5";
  const sectionClass = "bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6";

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen pt-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/speakers" className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-primary transition-colors mb-4">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Speakers
          </Link>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
            Create Speaker Profile
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Share your ministry with conference organizers and attendees across the Apostolic community.
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <div className={sectionClass}>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className={labelClass}>Full Name <span className="text-red-500">*</span></label>
                <input type="text" name="name" required value={form.name} onChange={handleChange} className={inputClass} placeholder="John Smith" />
              </div>
              <div>
                <label className={labelClass}>Role / Title</label>
                <input type="text" name="title" value={form.title} onChange={handleChange} className={inputClass} placeholder="Senior Pastor" />
              </div>
              <div>
                <label className={labelClass}>Ministry / Church</label>
                <input type="text" name="church_name" value={form.church_name} onChange={handleChange} className={inputClass} placeholder="New Life Church" />
              </div>
              <div>
                <label className={labelClass}>City</label>
                <input type="text" name="city" value={form.city} onChange={handleChange} className={inputClass} placeholder="St. Louis" />
              </div>
              <div>
                <label className={labelClass}>State</label>
                <input type="text" name="state" value={form.state} onChange={handleChange} className={inputClass} placeholder="Missouri" />
              </div>
              <div>
                <label className={labelClass}>Organization</label>
                <input type="text" name="organization" value={form.organization} onChange={handleChange} className={inputClass} placeholder="UPCI, ALJC, WPF, AFF, or Independent" />
              </div>
              <div>
                <label className={labelClass}>Photo URL</label>
                <input type="text" name="photo_url" value={form.photo_url} onChange={handleChange} className={inputClass} placeholder="https://..." />
              </div>
            </div>
          </div>

          {/* Ministry */}
          <div className={sectionClass}>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Ministry</h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Biography</label>
                <textarea name="bio" rows={4} value={form.bio} onChange={handleChange} className={`${inputClass} resize-none`} placeholder="Share about your ministry, calling, and experience..." />
              </div>
              <div>
                <label className={labelClass}>Ministry Focus / Topics</label>
                <input type="text" name="topics" value={form.topics} onChange={handleChange} className={inputClass} placeholder="Evangelism, Youth Ministry, Leadership (comma-separated)" />
                <p className="text-xs text-gray-400 mt-1">Separate topics with commas.</p>
              </div>
            </div>
          </div>

          {/* Online Presence */}
          <div className={sectionClass}>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Online Presence</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className={labelClass}>Website</label>
                <input type="text" name="website" value={form.website} onChange={handleChange} className={inputClass} placeholder="https://yourministry.com" />
              </div>
              <div>
                <label className={labelClass}>Facebook URL</label>
                <input type="text" name="facebook_url" value={form.facebook_url} onChange={handleChange} className={inputClass} placeholder="https://facebook.com/..." />
              </div>
              <div>
                <label className={labelClass}>Instagram URL</label>
                <input type="text" name="instagram_url" value={form.instagram_url} onChange={handleChange} className={inputClass} placeholder="https://instagram.com/..." />
              </div>
              <div>
                <label className={labelClass}>YouTube URL</label>
                <input type="text" name="youtube_url" value={form.youtube_url} onChange={handleChange} className={inputClass} placeholder="https://youtube.com/..." />
              </div>
              <div>
                <label className={labelClass}>X / Twitter</label>
                <input type="text" name="twitter" value={form.twitter} onChange={handleChange} className={inputClass} placeholder="@username" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>LinkedIn</label>
                <input type="text" name="linkedin" value={form.linkedin} onChange={handleChange} className={inputClass} placeholder="https://linkedin.com/in/..." />
              </div>
            </div>
          </div>

          {/* Media & Resources */}
          <div className={sectionClass}>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Media & Resources (Optional)</h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Featured YouTube Video ID</label>
                <input type="text" name="youtube_video_id" value={form.youtube_video_id} onChange={handleChange} className={inputClass} placeholder="dQw4w9WgXcQ" />
                <p className="text-xs text-gray-400 mt-1">The ID from your YouTube video URL (e.g., youtube.com/watch?v=<strong>VIDEO_ID</strong>)</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>Book URL</label>
                  <input type="text" name="book_url" value={form.book_url} onChange={handleChange} className={inputClass} placeholder="https://..." />
                </div>
                <div>
                  <label className={labelClass}>Amazon URL</label>
                  <input type="text" name="amazon_url" value={form.amazon_url} onChange={handleChange} className={inputClass} placeholder="https://amazon.com/..." />
                </div>
                <div>
                  <label className={labelClass}>Resources URL</label>
                  <input type="text" name="resources_url" value={form.resources_url} onChange={handleChange} className={inputClass} placeholder="https://..." />
                </div>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex flex-col sm:flex-row gap-3 pb-8">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit Speaker Profile'}
            </button>
            <Link
              to="/speakers"
              className="flex-1 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-center"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SpeakerApplyPage;
